import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  type ErrorsExportVisibleStrategy,
  downloadErrorsAsCSV,
  downloadErrorsAsXLSX,
} from "@/lib/export-errors";
import { ChevronDown, Download } from "lucide-react";

type ImportErrorsExportMenuProps = {
  errors: Array<Record<string, unknown>>;
  filenamePrefix: string;
  /** Columns that match the on-screen error table (or `simple` for row + message only). */
  visibleStrategy: ErrorsExportVisibleStrategy;
  /**
   * Upload flows: single export shape (all row data columns). Dashboard keeps CSV/Excel ×
   * visible vs all columns.
   */
  uploadVariant?: boolean;
};

/**
 * Dropdown for exporting import/dry-run errors as CSV or Excel.
 *
 * Default (e.g. jurisdiction dashboard): four options — CSV/Excel × visible / all columns.
 * With `uploadVariant`, two options — CSV and Excel with full column export only.
 */
export function ImportErrorsExportMenu({
  errors,
  filenamePrefix,
  visibleStrategy,
  uploadVariant = false,
}: ImportErrorsExportMenuProps) {
  if (uploadVariant) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
          >
            <Download className="mr-2 h-4 w-4" />
            Export
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() =>
              downloadErrorsAsCSV(errors, filenamePrefix, {
                mode: "all",
                visible: visibleStrategy,
              })
            }
          >
            Download CSV
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              downloadErrorsAsXLSX(errors, filenamePrefix, {
                mode: "all",
                visible: visibleStrategy,
              })
            }
          >
            Download Excel
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="shrink-0">
          <Download className="mr-2 h-4 w-4" />
          Export
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={() =>
            downloadErrorsAsCSV(errors, filenamePrefix, {
              mode: "visible",
              visible: visibleStrategy,
            })
          }
        >
          CSV — visible columns
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            downloadErrorsAsCSV(errors, filenamePrefix, {
              mode: "all",
              visible: visibleStrategy,
            })
          }
        >
          CSV — all columns
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            downloadErrorsAsXLSX(errors, filenamePrefix, {
              mode: "visible",
              visible: visibleStrategy,
            })
          }
        >
          Excel — visible columns
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            downloadErrorsAsXLSX(errors, filenamePrefix, {
              mode: "all",
              visible: visibleStrategy,
            })
          }
        >
          Excel — all columns
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
